import { useCallback, useEffect, useState } from 'react'
import type { ConfrerieDTO } from '@whatremains/shared'
import { EffetObjet, NomObjet } from '../composants/NomObjet'
import { api, ErreurApi } from '../api'
import { IconeObjet } from '../composants/IconeObjet'
import { Echange } from '../composants/Comparaison'

/**
 * Les confréries.
 *
 * L'écran doit faire sentir une seule chose, et elle est structurelle : **on
 * est des siens ou on ne l'est pas.** Ça ne dépend ni de la ville, ni de la
 * réputation, ni de ce qu'on a fait. C'est le seul endroit du jeu où l'accueil
 * ne se négocie pas, et c'est pour ça que la maison du joueur passe en tête,
 * séparée du reste par autre chose qu'un titre.
 *
 * Les autres maisons ne sont pas grisées ni marquées « verrouillé » : on lit ce
 * qu'on y entend, et ce qu'on y entend suffit à comprendre.
 */

export function Confrerie({ id, surChangement }: { id: string; surChangement: () => void }) {
  const [maisons, setMaisons] = useState<ConfrerieDTO[]>([])
  const [erreur, setErreur] = useState<string | null>(null)
  const [note, setNote] = useState<string | null>(null)

  const recharger = useCallback(async () => setMaisons(await api.confreries(id)), [id])
  useEffect(() => { void recharger() }, [recharger])

  async function acheter(c: ConfrerieDTO, modeleCode: string, nom: string, prix: number) {
    setErreur(null)
    try {
      await api.acheterConfrerie(id, c.code, modeleCode)
      setNote(`${nom}, ${prix} or.`)
      await recharger()
      surChangement()
    } catch (e) {
      setNote(null)
      setErreur(e instanceof ErreurApi ? e.message : "Quelque chose n'a pas abouti.")
    }
  }

  const mienne = maisons.find((m) => m.sien)
  const autres = maisons.filter((m) => !m.sien)

  return (
    <div className="cadre">
      <div className="entete">
        <div className="surtitre">Maisons</div>
        <h1>{mienne ? mienne.nom : 'Les maisons'}</h1>
        {mienne && <p className="attenue" style={{ maxWidth: '62ch' }}>{mienne.texte}</p>}
        {erreur && <p className="erreur">{erreur}</p>}
        {note && <p className="note">{note}</p>}
      </div>

      {mienne && (
        <section>
          <div className="surtitre">
            {mienne.ici ? 'Tu y es' : 'Chez toi'}
            {' · '}
            {mienne.lieux.map((l) => `${l.nom}${l.siege ? ' (siège)' : ''}`).join(', ')}
          </div>

          <p className="accueil">{mienne.accueil}</p>

          {mienne.maitre && (
            <p className="attenue" style={{ maxWidth: '64ch' }}>
              <b>{mienne.maitre.nom}</b>. {mienne.maitre.texte}
            </p>
          )}

          {!mienne.ici && (
            <p className="tenu">
              Le dépôt ne s’ouvre que sur place.
            </p>
          )}

          {mienne.ici && mienne.stock.length > 0 && (
            <>
              <div className="surtitre" style={{ marginTop: 28 }}>
                Ce qu’ils ont et que personne d’autre n’a
              </div>
              <div className="offres">
                {mienne.stock.map((o) => (
                  <div className="offre" key={o.id}>
                    <span className="offre-glyphe">
                      <IconeObjet
                        objet={{ code: o.code, categorie: o.categorie, famille: o.famille,
                  palier: o.palier, materiau: '' }}
                        taille={34}
                      />
                    </span>
                    <div className="offre-corps">
                      <NomObjet o={o} />
                      <EffetObjet effet={o.effetTexte} />
                      {o.description && <i>{o.description}</i>}
                      <span className="offre-chiffres">
                        niveau {o.niveauRequis}
                        {o.degatsMax > 0 && ` · ${o.degatsMin} à ${o.degatsMax} de dégâts`}
                        {o.horsDePortee && (
                          <em className="avertissement"> · trop haut pour toi</em>
                        )}
                      </span>
                      <Echange echange={o.echange} />
                    </div>
                    <div className="offre-prix">
                      <b className={o.tropCher ? 'tenu' : undefined}>{o.prix} or</b>
                      <button
                        className="discret"
                        disabled={o.tropCher || o.horsDePortee}
                        onClick={() => void acheter(mienne, o.id, o.nom, o.prix)}
                      >
                        {o.tropCher ? 'trop cher' : 'prendre'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </section>
      )}

      <section>
        <div className="surtitre">Les autres</div>
        <p className="attenue" style={{ maxWidth: '64ch' }}>
          Elles existent, on peut y entrer, et ça ne veut pas dire qu’on y est reçu.
        </p>

        <div className="maisons">
          {autres.map((c) => (
            <div className={`maison${c.ici ? ' maison-ici' : ''}`} key={c.code}>
              <div className="maison-tete">
                <h3>{c.nom}</h3>
                <div className="donjon-meta">
                  <span>{c.classe}</span>
                  {c.lieux.map((l) => (
                    <span key={l.code}>{l.nom}</span>
                  ))}
                  {c.ici && <span className="fait">tu y es</span>}
                </div>
              </div>
              <p className="accueil accueil-froid">{c.accueil}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  )
}
